import * as React from 'react';
import type { IAuditLog } from '../../../src/engine/AuditLog';
import type { IGraphClient } from '../../../src/services/GraphService';
import type { IWorkspace } from '../../api/src/workspace';
import { isPaused } from '../../api/src/workspace';
import { checkInbox, type IInboxProgress, type IInboxResult } from './inbox';
import { browserInboxDeps } from './browserInbox';
import type { ReviewList } from '../ReviewList';
import { errorText } from '../app/context';

export interface IInboxState {
  checking: boolean;
  progress?: IInboxProgress;
  lastChecked?: Date;
  lastResult?: IInboxResult;
  error?: string;
  checkNow: () => Promise<IInboxResult | undefined>;
}

const EVERY_MS: number = 5 * 60 * 1000;

/**
 * Checks the Inbox when the app opens and every 5 minutes while it stays open (and filing isn't
 * paused). With filing in the background on, the filing service does this around the clock, so the
 * app doesn't (Check now still works).
 */
export function useInbox(opts: {
  graph: IGraphClient;
  workspace?: IWorkspace;
  audit?: IAuditLog;
  /** The Filing Review list in SharePoint, kept in step with the queue. */
  reviewList?: ReviewList;
  user: string;
  onDone: (r: IInboxResult) => void;
}): IInboxState {
  const [state, setState] = React.useState<Omit<IInboxState, 'checkNow'>>({ checking: false });
  const busy = React.useRef<boolean>(false);
  const optsRef = React.useRef(opts);
  optsRef.current = opts;

  const checkNow = React.useCallback(async (): Promise<IInboxResult | undefined> => {
    const { graph, workspace, audit, reviewList, user } = optsRef.current;
    if (busy.current || !workspace || !workspace.setupDone || !audit) {
      return undefined;
    }
    busy.current = true;
    setState(s => ({ ...s, checking: true, error: undefined, progress: undefined }));
    try {
      const r: IInboxResult = await checkInbox({ graph, workspace, audit, user }, browserInboxDeps(workspace.driveId, reviewList), p => setState(s => ({ ...s, progress: p })));
      setState({ checking: false, lastChecked: new Date(), lastResult: r });
      optsRef.current.onDone(r);
      return r;
    } catch (e) {
      setState(s => ({ ...s, checking: false, lastChecked: new Date(), error: errorText(e) }));
      return undefined;
    } finally {
      busy.current = false;
    }
  }, []);

  const driveId: string | undefined = opts.workspace?.driveId;
  const auto: boolean = !!opts.workspace?.setupDone && !!opts.workspace && !isPaused(opts.workspace) && !state.lastResult?.trialEnded
    && opts.workspace.background?.enabled !== true;
  React.useEffect(() => {
    if (!auto) {
      return undefined;
    }
    void checkNow();
    const timer = setInterval(() => { void checkNow(); }, EVERY_MS);
    return () => clearInterval(timer);
  }, [auto, driveId, checkNow]);

  return { ...state, checkNow };
}
